import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// ============================================================================
// note-client-mod -- 笔记公开列表页
//
// 路径：/notes
// 给访客浏览公开分享的笔记列表（按标签过滤）。
// ============================================================================
import * as React from 'react';
import { useTheme } from '../styles/use-theme.js';
import { useResponsive } from '../styles/use-responsive.js';
import { radius, spacing, typography, transition } from '../styles/theme.js';
import { KEYFRAMES_CSS } from '../styles/animations.js';
import { getNoteApiClient } from '../note-api-client.js';
import { NoteListItem } from '../components/note-list-item.js';
import { EmptyIcon } from '../components/icons.js';
import { useRoute } from '@hvsin/mod-route';
export function NoteListPage(props) {
    const { initialData } = props;
    const { theme } = useTheme();
    const { isMobile } = useResponsive();
    const { Link } = useRoute();
    const [tags, setTags] = React.useState([]);
    const [activeTag, setActiveTag] = React.useState('');
    const [notes, setNotes] = React.useState(initialData?.data ?? []);
    const [pagination, setPagination] = React.useState(initialData?.pagination ?? null);
    const [loading, setLoading] = React.useState(!initialData);
    const [error, setError] = React.useState(null);
    const [page, setPage] = React.useState(1);
    // 注入全局动画样式
    React.useEffect(() => {
        const styleId = 'note-list-page-keyframes';
        if (!document.getElementById(styleId)) {
            const style = document.createElement('style');
            style.id = styleId;
            style.textContent = KEYFRAMES_CSS;
            document.head.appendChild(style);
        }
    }, []);
    const fetchNotes = React.useCallback(async (targetPage, tag) => {
        setLoading(true);
        setError(null);
        try {
            const result = await getNoteApiClient().getPublicNotes({ page: targetPage, pageSize: 12, tag: tag || undefined });
            setNotes(result.data);
            setPagination(result.pagination);
        }
        catch (err) {
            setError(err instanceof Error ? err.message : '加载失败');
            setNotes([]);
        }
        finally {
            setLoading(false);
        }
    }, []);
    // 加载标签聚合（从公开列表中的笔记里简单统计）
    React.useEffect(() => {
        if (!initialData) {
            fetchNotes(page, activeTag);
        }
        // 从当前页笔记统计标签（简化：仅展示这一页出现过的标签）
        const tagMap = new Map();
        notes.forEach(n => (n.tags ?? []).forEach(t => tagMap.set(t, (tagMap.get(t) ?? 0) + 1)));
        setTags(Array.from(tagMap.entries()).map(([tag, count]) => ({ tag, count })));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const handleTagClick = (tag) => {
        const nextTag = activeTag === tag ? '' : tag;
        setActiveTag(nextTag);
        setPage(1);
        fetchNotes(1, nextTag);
    };
    const handlePageChange = (nextPage) => {
        setPage(nextPage);
        fetchNotes(nextPage, activeTag);
    };
    return (_jsxs("div", { style: { minHeight: '100vh', backgroundColor: theme.bg.primary, fontFamily: typography.fontFamily, color: theme.text.primary }, children: [_jsxs("header", { style: { backgroundColor: theme.bg.secondary, borderBottom: `1px solid ${theme.border.divider}`, padding: isMobile ? '24px 16px 20px' : '40px 24px 30px', textAlign: 'center' }, children: [_jsx("h1", { style: { margin: '0 0 8px 0', fontSize: isMobile ? typography.fontSize['2xl'] : typography.fontSize['3xl'], fontWeight: typography.fontWeight.bold, color: theme.text.primary }, children: "\u516C\u5F00\u7B14\u8BB0" }), _jsx("p", { style: { margin: 0, fontSize: typography.fontSize.md, color: theme.text.tertiary }, children: "\u6D4F\u89C8\u793E\u533A\u516C\u5F00\u5206\u4EAB\u7684\u7B14\u8BB0" })] }), _jsxs("main", { style: { maxWidth: '880px', margin: '0 auto', padding: isMobile ? '20px 16px 40px' : '32px 24px 60px' }, children: [tags.length > 0 && (_jsx("div", { style: { display: 'flex', gap: spacing.sm, flexWrap: 'wrap', marginBottom: spacing.xl }, children: tags.map(({ tag, count }) => (_jsxs("button", { onClick: () => handleTagClick(tag), style: {
                                padding: '4px 12px',
                                borderRadius: radius.full,
                                border: `1px solid ${activeTag === tag ? theme.status.primary : theme.border.default}`,
                                backgroundColor: activeTag === tag ? theme.interaction.selected : theme.bg.secondary,
                                color: activeTag === tag ? theme.status.primary : theme.text.secondary,
                                fontSize: typography.fontSize.sm,
                                cursor: 'pointer',
                                transition: `background-color ${transition.fast}, color ${transition.fast}, border-color ${transition.fast}`,
                            }, children: ["#", tag, " (", count, ")"] }, tag))) })), loading && (_jsx("div", { style: { padding: '80px 0', textAlign: 'center', color: theme.text.tertiary, fontSize: typography.fontSize.md }, children: _jsx("span", { style: { animation: 'note-pulse 1.5s ease-in-out infinite', display: 'inline-block' }, children: "\u52A0\u8F7D\u4E2D..." }) })), error && (_jsx("div", { style: { padding: spacing.lg, backgroundColor: theme.status.dangerBg, border: `1px solid ${theme.status.danger}40`, borderRadius: radius.md, color: theme.status.danger, fontSize: typography.fontSize.base }, children: error })), !loading && !error && notes.length === 0 && (_jsxs("div", { style: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 0', gap: spacing.md, animation: 'note-fade-in-up 0.3s ease forwards' }, children: [_jsx("span", { style: { color: theme.text.tertiary }, children: _jsx(EmptyIcon, {}) }), _jsx("p", { style: { fontSize: typography.fontSize.lg, color: theme.text.secondary, margin: 0 }, children: "\u6682\u65E0\u516C\u5F00\u7B14\u8BB0" })] })), !loading && !error && notes.length > 0 && (_jsx("div", { style: { display: 'flex', flexDirection: 'column', gap: spacing.md }, children: notes.map(note => (_jsx(Link, { to: `/notes/${note._id}`, style: { textDecoration: 'none', color: 'inherit', display: 'block' }, children: _jsx(NoteListItem, { note: { ...note, visibility: 'public' } }) }, note._id))) })), !loading && pagination && pagination.totalPages > 1 && (_jsxs("div", { style: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: spacing.lg, marginTop: '48px', paddingTop: spacing.xl }, children: [_jsx("button", { onClick: () => handlePageChange(Math.max(1, pagination.page - 1)), disabled: pagination.page <= 1, style: {
                                    padding: '8px 16px',
                                    border: `1px solid ${theme.border.default}`,
                                    borderRadius: radius.md,
                                    backgroundColor: theme.bg.secondary,
                                    fontSize: typography.fontSize.base,
                                    color: theme.text.secondary,
                                    cursor: 'pointer',
                                }, children: "\u2190 \u4E0A\u4E00\u9875" }), _jsxs("span", { style: { fontSize: typography.fontSize.base, color: theme.text.tertiary }, children: [pagination.page, " / ", pagination.totalPages] }), _jsx("button", { onClick: () => handlePageChange(Math.min(pagination.totalPages, pagination.page + 1)), disabled: pagination.page >= pagination.totalPages, style: {
                                    padding: '8px 16px',
                                    border: `1px solid ${theme.border.default}`,
                                    borderRadius: radius.md,
                                    backgroundColor: theme.bg.secondary,
                                    fontSize: typography.fontSize.base,
                                    color: theme.text.secondary,
                                    cursor: 'pointer',
                                }, children: "\u4E0B\u4E00\u9875 \u2192" })] }))] })] }));
}
//# sourceMappingURL=note-list-page.js.map